<template>
    <Teleport v-if="canTeleport" to="body">
        <Transition name="photo-lightbox">
            <div
                v-if="open && currentItem"
                class="photo-lightbox"
                :class="{ 'photo-lightbox--framed': variant === 'framed' }"
                role="dialog"
                aria-modal="true"
                aria-label="Просмотр фото"
                @keydown.escape.prevent="close"
            >
                <button
                    type="button"
                    class="photo-lightbox__backdrop"
                    aria-label="Закрыть"
                    @click="close"
                />

                <div class="photo-lightbox__stage">
                    <button
                        type="button"
                        class="photo-lightbox__close"
                        aria-label="Закрыть"
                        @click="close"
                    >
                        <svg width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <button
                        v-if="hasMultiple"
                        type="button"
                        class="photo-lightbox__nav photo-lightbox__nav--prev"
                        aria-label="Предыдущее фото"
                        :disabled="activeIndex <= 0"
                        @click="prev"
                    >
                        ‹
                    </button>

                    <figure class="photo-lightbox__figure">
                        <img
                            :src="currentItem.url"
                            :alt="currentItem.caption || 'Фото'"
                            class="photo-lightbox__img"
                            decoding="async"
                        />
                        <figcaption v-if="currentItem.caption" class="photo-lightbox__caption">
                            {{ currentItem.caption }}
                        </figcaption>
                    </figure>

                    <button
                        v-if="hasMultiple"
                        type="button"
                        class="photo-lightbox__nav photo-lightbox__nav--next"
                        aria-label="Следующее фото"
                        :disabled="activeIndex >= items.length - 1"
                        @click="next"
                    >
                        ›
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    open: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    index: { type: Number, default: 0 },
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'framed'].includes(value),
    },
});

const emit = defineEmits(['close', 'update:index']);

const canTeleport = ref(false);
const activeIndex = ref(0);

onMounted(() => {
    canTeleport.value = true;
});

const hasMultiple = computed(() => props.items.length > 1);
const currentItem = computed(() => props.items[activeIndex.value] || null);

watch(
    () => [props.open, props.index, props.items.length],
    () => {
        if (!props.open) return;
        const max = Math.max(0, props.items.length - 1);
        activeIndex.value = Math.max(0, Math.min(props.index || 0, max));
    },
    { immediate: true }
);

watch(
    () => props.open,
    (isOpen) => {
        if (typeof document === 'undefined') return;
        document.body.style.overflow = isOpen ? 'hidden' : '';
    }
);

function close() {
    emit('close');
}

function setIndex(next) {
    const max = Math.max(0, props.items.length - 1);
    const clamped = Math.max(0, Math.min(next, max));
    activeIndex.value = clamped;
    emit('update:index', clamped);
}

function prev() {
    if (activeIndex.value > 0) setIndex(activeIndex.value - 1);
}

function next() {
    if (activeIndex.value < props.items.length - 1) setIndex(activeIndex.value + 1);
}

function onKeydown(event) {
    if (!props.open) return;
    if (event.key === 'Escape') {
        close();
        return;
    }
    if (!hasMultiple.value) return;
    if (event.key === 'ArrowLeft') prev();
    if (event.key === 'ArrowRight') next();
}

onMounted(() => {
    document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown);
    if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
    }
});
</script>
